import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { TripCard } from "@/components/TripCard";
import { EventCard } from "@/components/EventCard";
import { DropSpotlight } from "@/components/DropSpotlight";
import { CommunityFlow } from "@/components/CommunityFlow";
import { StatsStrip } from "@/components/StatsStrip";
import { BrandStory } from "@/components/BrandStory";
import { TaglineMoment } from "@/components/TaglineMoment";
import { SafetySection } from "@/components/SafetySection";
import { Testimonials } from "@/components/Testimonials";
import { Marquee } from "@/components/Marquee";
import { WaitlistForm } from "@/components/WaitlistForm";
import { ContactForm } from "@/components/ContactForm";
import { MaskedLines, MaskUp } from "@/components/Kinetic";
import { ConnectSection } from "@/components/ConnectSection";
import { Button } from "@/components/ui/button";

const HERO_IMG = "/images/hero.jpg";

const SOCIAL_POSTS = [
  { img: "/images/social/social-1.jpg", caption: "Your next crew could start here." },
  { img: "/images/social/social-2.jpg", caption: "Adventure is better when you don't have to do it alone." },
  { img: "/images/social/social-3.jpg", caption: "Future Roamies loading..." },
  { img: "/images/social/social-4.jpg", caption: "Where should we roam next?" },
];

const CHAPTERS = [
  { n: "01", title: "The problem", text: "Group travel was broken: overpriced tour buses with strangers twice your age, or solo trips where you eat dinner alone scrolling your phone. We wanted a third option — so we built it." },
  { n: "02", title: "The fix", text: "Small curated crews, always ages 18–45, with a dedicated host who runs the show and an itinerary that skips the tourist traps. You book solo. You never travel alone." },
  { n: "03", title: "The family", text: "The group chat opens weeks before takeoff, so by the time you land, you're meeting friends — not strangers. Show up solo on Friday. Leave with a family on Sunday." },
];

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function HomePage() {
  useSEO("Roamies — Small-Group Trips for Solo Travelers 18–45", "Show up solo. Leave with a family. Curated small-group trips and local social events for solo travelers ages 18–45.");
  const [trips, setTrips] = useState([]);
  const [events, setEvents] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    api.get("/trips").then((r) => setTrips(r.data)).catch(() => {});
    api.get("/trips/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const primaryTrips = trips
    .filter((t) => ["coming_soon", "waitlist_open"].includes(t.status) && (t.collection || "future") !== "future")
    .slice(0, 6);
  const irlPreview = events.filter((e) => e.status !== "completed").slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[95vh] flex items-end overflow-hidden bg-ink" data-testid="hero-section">
        <div className="absolute inset-0">
          <motion.img
            src={HERO_IMG}
            alt="Young travelers on a group roadtrip at golden hour"
            className="w-full h-full object-cover"
            initial={{ scale: 1.1, filter: "brightness(0.65)" }}
            animate={{ scale: 1, filter: "brightness(1)" }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-ink/35" />
        <motion.div
          className="absolute inset-0 bg-ink z-30 pointer-events-none"
          initial={{ y: 0 }}
          animate={{ y: "-101%" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-20 pt-48 w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}
            className="text-xs font-bold tracking-[0.25em] uppercase text-cream/80 mb-6"
            data-testid="hero-overline"
          >
            Small-group adventures for solo travelers · Ages 18–45
          </motion.p>
          <h1
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] leading-[0.88] uppercase text-cream"
            data-testid="hero-heading"
          >
            <MaskedLines
              delay={0.45}
              lines={[<>Show up</>, <>solo.</>, <>Leave with</>, <span className="text-flame">a family.</span>]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-6 max-w-xl text-base md:text-lg text-cream/85"
          >
            Curated small-crew trips and local social events for people who arrive alone — and leave with real friendships.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.1 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Button asChild data-testid="hero-explore-trips-btn"
              className="bg-flame hover:bg-flame/90 text-white rounded-none h-14 px-8 font-bold uppercase tracking-widest text-sm transition-transform hover:-translate-y-0.5">
              <Link to="/trips">Explore Trips <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild data-testid="hero-join-community-btn"
              className="bg-transparent border-2 border-cream text-cream hover:bg-cream hover:text-ink rounded-none h-14 px-8 font-bold uppercase tracking-widest text-sm transition-colors">
              <a href="#waitlist">Join the Community</a>
            </Button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-8 text-[11px] font-bold uppercase tracking-[0.25em] text-cream/60"
            data-testid="hero-trust-line"
          >
            Small groups ✦ Curated experiences ✦ Ages 18–45
          </motion.p>
        </div>
        <motion.a
          href="#usa-trips"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-cream/70 hover:text-flame transition-colors"
          aria-label="Scroll to trips"
          data-testid="hero-scroll-indicator"
        >
          <ArrowDown className="w-5 h-5 animate-scroll-hint" />
        </motion.a>
      </section>

      <Marquee editorial />

      {/* NEXT ROAMIES DROP */}
      <DropSpotlight trip={featured} />

      {/* PRIMARY TRIPS — the launch five */}
      <section id="usa-trips" className="pb-16 sm:pb-20" data-testid="usa-trips-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp} className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">The Launch Lineup</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
                <MaskUp>Five trips.</MaskUp><MaskUp delay={0.08}>Pick your first crew.</MaskUp>
              </h2>
            </div>
            <Link to="/trips" data-testid="view-future-adventures-link"
              className="text-sm font-bold uppercase tracking-widest text-ink border-b-2 border-flame pb-1 hover:text-flame transition-colors w-fit">
              View all future adventures →
            </Link>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {primaryTrips.map((trip, i) => (
              <motion.div key={trip.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.07 }}>
                <TripCard trip={trip} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* KEY BRAND INFO */}
      <StatsStrip />

      {/* COMMUNITY LADDER / HOW IT WORKS */}
      <CommunityFlow />

      {/* WHY ROAMIES EXISTS — brand story */}
      <BrandStory />

      {/* ROAMIES IRL PREVIEW */}
      <section className="py-16 sm:py-20" data-testid="irl-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp} className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">Roamies IRL</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
                <MaskUp>Meet the crew</MaskUp><MaskUp delay={0.08}>before the trip.</MaskUp>
              </h2>
              <p className="mt-4 max-w-xl text-ink/70 leading-relaxed">
                Local socials — dinners, rooftops, beach days — where solo people meet the community before committing to a bigger trip.
              </p>
            </div>
            <Link to="/irl" data-testid="irl-view-all-link"
              className="text-sm font-bold uppercase tracking-widest text-ink border-b-2 border-flame pb-1 hover:text-flame transition-colors w-fit">
              All IRL events →
            </Link>
          </motion.div>
          {irlPreview.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {irlPreview.map((ev, i) => (
                <motion.div key={ev.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.07 }}>
                  <EventCard event={ev} onRsvp={() => { window.location.href = "/irl"; }} />
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-ink/50 font-semibold py-6" data-testid="irl-empty-home">First IRL events are being planned — join the waitlist to hear when your city gets one.</p>
          )}
        </div>
      </section>

      {/* SAFETY & TRUST */}
      <SafetySection />

      {/* TESTIMONIALS (auto-hidden until real stories exist) */}
      <Testimonials />

      {/* SOCIAL FEED */}
      <section className="bg-ink text-cream noise-bg py-16 sm:py-20" data-testid="social-feed-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp} className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">Follow the Roamies Journey</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
                <MaskUp>The first crews</MaskUp><MaskUp delay={0.08}>are forming now.</MaskUp>
              </h2>
            </div>
            <p className="text-sm text-cream/60 font-semibold">Real trip content drops after our first Roamies adventures</p>
          </motion.div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {SOCIAL_POSTS.map((post, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                data-testid={`social-post-${i}`}
                className="group relative aspect-square overflow-hidden border border-cream/10">
                <img src={post.img} alt={post.caption} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-sm font-semibold text-cream">{post.caption}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TAGLINE MOMENT — signature brand beat */}
      <TaglineMoment />

      {/* WAITLIST */}
      <section id="waitlist" className="py-16 sm:py-20" data-testid="waitlist-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div {...fadeUp}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">Join the Waitlist</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              <MaskUp>Get first dibs</MaskUp><MaskUp delay={0.08}>on every drop.</MaskUp>
            </h2>
            <p className="mt-6 text-ink/70 max-w-md leading-relaxed">
              Trips and IRL events are small on purpose and fill fast. Join the waitlist, tell us what you're into, and you'll be first in line — no payment needed today.
            </p>
            <ul className="mt-8 space-y-3 text-sm font-semibold">
              <li className="flex items-center gap-3"><span className="w-2 h-2 bg-flame" /> Early access before public booking</li>
              <li className="flex items-center gap-3"><span className="w-2 h-2 bg-flame" /> Trip + local event announcements first</li>
              <li className="flex items-center gap-3"><span className="w-2 h-2 bg-flame" /> Instant email confirmation</li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="bg-white border border-black/10 p-6 sm:p-10 shadow-brutal">
            <WaitlistForm trips={trips} events={events} />
          </motion.div>
        </div>
      </section>

      <Marquee dark={false} />

      {/* ABOUT / MANIFESTO */}
      <section id="about" className="py-16 sm:py-24" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp} className="mb-16 sm:mb-20">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">The Roamies Manifesto</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              <MaskUp>Travel alone,</MaskUp><MaskUp delay={0.08}>never lonely.</MaskUp>
            </h2>
          </motion.div>
          <div className="border-t border-ink/10">
            {CHAPTERS.map((c, i) => (
              <motion.div key={c.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                data-testid={`manifesto-chapter-${c.n}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 py-12 sm:py-16 border-b border-ink/10">
                <div className="md:col-span-3">
                  <span className="chapter-num font-display text-7xl sm:text-8xl lg:text-9xl leading-none">{c.n}</span>
                </div>
                <div className="md:col-span-4 flex items-start">
                  <h3 className="font-display text-3xl sm:text-4xl uppercase leading-none">{c.title}</h3>
                </div>
                <div className="md:col-span-5 flex items-start">
                  <p className="text-ink/70 leading-relaxed">{c.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="grid grid-cols-3 gap-4 pt-14 max-w-2xl">
            <div className="border-l-2 border-flame pl-4">
              <p className="font-display text-3xl sm:text-4xl" data-testid="stat-group-size">Small</p>
              <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mt-1">Curated crews</p>
            </div>
            <div className="border-l-2 border-flame pl-4">
              <p className="font-display text-3xl sm:text-4xl" data-testid="stat-age-range">18–45</p>
              <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mt-1">Age range</p>
            </div>
            <div className="border-l-2 border-flame pl-4">
              <p className="font-display text-3xl sm:text-4xl" data-testid="stat-price-from">12</p>
              <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mt-1">Max per trip</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CONNECT WITH ROAMIES */}
      <ConnectSection />

      {/* CONTACT */}
      <section id="contact" className="bg-ink text-cream noise-bg py-16 sm:py-20" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div {...fadeUp}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">Contact</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              <MaskUp>Questions?</MaskUp><MaskUp delay={0.08}>We reply fast.</MaskUp>
            </h2>
            <p className="mt-6 text-cream/70 max-w-md leading-relaxed">
              Wondering how drops work, whether you can bring a friend, or when your city gets a Roamies IRL? Drop us a line.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="bg-cream text-ink p-6 sm:p-10">
            <ContactForm />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
